import { Chart as ChartJS, registerables } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { SupportedCurrency } from '../../../../../domain/currency';
import { CombinedMonthlyProfitStats } from '../../../../../domain/stats';
import { getPolishMonthName } from '../../../../../utils/polishMonthNames';

ChartJS.register(...registerables);

type MultipleMonthCumulativeProfitStatsProps = {
    multipleMonthStats: CombinedMonthlyProfitStats;
    selectedCurrency: SupportedCurrency;
}

export const MultipleMonthCumulativeProfitStats = ({
    multipleMonthStats,
    selectedCurrency,
}: MultipleMonthCumulativeProfitStatsProps) => {
    const statsForSelectedCurrency = multipleMonthStats[selectedCurrency];

    if (!statsForSelectedCurrency) {
        return null;
    }

    const {
        profits,
        cumulativeProfits,
        minProfit,
        maxProfit,
    } = statsForSelectedCurrency;

    // Format labels as Month Year (e.g., "Maj 2024")
    const labels = profits.map(({ month, year }) => `${getPolishMonthName(month)} ${year}`);

    // Calculate chart min/max values for better visualization
    const barChartMin = minProfit < 0 ? Number((Math.floor(minProfit * 1.3)).toFixed(2)) : 0;
    const barChartMax = Number((Math.ceil(maxProfit * 1.3)).toFixed(2));

    // Prepare chart data
    const chartData = {
        labels: labels,
        datasets: [
            {
                type: 'bar',
                label: `Marża miesięczna (${selectedCurrency})`,
                data: profits.map(({ value }) => value),
                backgroundColor: 'rgba(54, 162, 235, 0.7)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
                yAxisID: 'yBar',
                barPercentage: 0.5,
                maxBarThickness: 150,
            },
            {
                type: 'line',
                label: `Marża skumulowana (${selectedCurrency})`,
                data: cumulativeProfits,
                borderColor: 'rgba(75, 192, 192, 1)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderWidth: 2,
                tension: 0.2,
                fill: true,
                yAxisID: 'yLine',
            },
        ],
    };

    const options = {
        responsive: true,
        animation: false,
        plugins: {
            tooltip: {
                callbacks: {
                    label: function (context: any) {
                        // Formatter for tooltips with proper currency formatting
                        const labelPrefix = context.dataset.type === 'line' ? 'Marża skumulowana' : 'Marża miesięczna';
                        const formattedValue = context.raw.toLocaleString('pl-PL', {
                            style: 'currency',
                            currency: selectedCurrency,
                        });

                        // Return only value for line chart (cumulative)
                        if (context.dataset.type === 'line') {
                            return `${labelPrefix}: ${formattedValue}`;
                        }

                        // Return value and number of active orders for bar chart (monthly)
                        const dataIndex = context.dataIndex;
                        const activeOrders = profits[dataIndex].numberOfActiveOrders;
                        return [
                            `${labelPrefix}: ${formattedValue}`,
                            `Liczba zleceń: ${activeOrders}`
                        ];
                    },
                },
            },
            legend: {
                position: 'top',
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Miesiące',
                },
            },
            yLine: {
                beginAtZero: true,
                position: 'right',
                title: {
                    display: true,
                    text: `Marża skumulowana (${selectedCurrency})`,
                },
                ticks: {
                    callback: (value: any) => `${value} ${selectedCurrency}`,
                },
            },
            yBar: {
                beginAtZero: barChartMin >= 0,
                position: 'left',
                title: {
                    display: true,
                    text: `Marża miesięczna (${selectedCurrency})`,
                },
                suggestedMin: barChartMin,
                suggestedMax: barChartMax,
                ticks: {
                    callback: (value: any) => `${value} ${selectedCurrency}`,
                },
                grid: {
                    drawOnChartArea: false,
                },
            },
        },
    };

    return (
        <>
            {/* @ts-ignore */}
            <Chart type="bar" style={{ maxHeight: 400 }} data={chartData} options={options} />
        </>
    );
};
